import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

const STRENGTH_COLOR = {
  Strong: "text-ember-400 border-ember-500/60",
  Moderate: "text-gold-400 border-gold-500/60",
  Weak: "text-forge-100/55 border-white/20",
};

export default function ResearchView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [handoffStance, setHandoffStance] = useState(null);
  const [copiedSearch, setCopiedSearch] = useState("");

  useEffect(() => {
    api.get(`/research/${id}`).then(({ data }) => setR(data)).catch(() => toast.error("Could not load this investigation."));
  }, [id]);

  const takeToDebate = async (stance) => {
    setHandoffStance(stance);
    try {
      const { data } = await api.post(`/research/${id}/debate`, { stance });
      navigate(`/debate/${data.debate_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the debate.");
      setHandoffStance(null);
    }
  };

  const copySearch = async (searchString) => {
    try {
      await navigator.clipboard.writeText(searchString);
      setCopiedSearch(searchString);
      toast.success("Search string copied.");
      window.setTimeout(() => setCopiedSearch(""), 1800);
    } catch {
      toast.error("Could not copy search string.");
    }
  };

  if (!r) {
    return (
      <div className="min-h-screen bg-forge-900 text-forge-100 flex items-center justify-center">
        <div className="text-sm tracking-[0.3em] uppercase text-ember-500 animate-pulse">Loading the field…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500">Research Mentor · {r.mode === "reading_linked" ? "Reading-linked" : "Stand-alone"}</div>
          <ModelBadge result={r} />
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mb-6" data-testid="research-view-topic">{r.topic}</h1>

        {/* Overview */}
        {r.overview && (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-3">Topic Overview</div>
            <p className="font-reading text-lg sm:text-xl leading-relaxed text-forge-100/90 border-l-2 border-ember-500 pl-6" data-testid="research-overview">
              {r.overview}
            </p>
          </section>
        )}

        {/* Perspectives */}
        {r.perspectives?.length > 0 && (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-2">Major Perspectives</div>
            <p className="text-xs text-forge-100/55 mb-6 max-w-2xl">Presented in parallel, never ranked. Read each carefully — including the ones that challenge your priors.</p>
            <div className="grid md:grid-cols-2 gap-px bg-white/5" data-testid="research-perspectives">
              {r.perspectives.map((p, i) => (
                <article key={i} className="bg-forge-900 p-6 hover:bg-forge-800 transition-colors">
                  <h3 className="font-serif text-xl text-forge-100 mb-4">{p.name}</h3>
                  <Detail label="Core claims" body={p.core_claims} />
                  <Detail label="Common evidence" body={p.common_evidence} />
                  <Detail label="Common critiques" body={p.common_critiques} />
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Key questions */}
        {r.key_questions?.length > 0 && (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Key Questions to Ask</div>
            <ol className="space-y-3 list-none" data-testid="research-questions">
              {r.key_questions.map((q, i) => (
                <li key={i} className="flex gap-4 border-l-2 border-white/10 pl-5 py-1">
                  <span className="font-mono text-xs text-ember-500 mt-1.5">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-reading text-base sm:text-lg text-forge-100/90">{q}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Evidence gaps */}
        {r.evidence_gaps?.length > 0 && (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Evidence Gaps</div>
            <ul className="space-y-2" data-testid="research-gaps">
              {r.evidence_gaps.map((g, i) => (
                <li key={i} className="flex gap-3 text-forge-100/85">
                  <span className="text-ember-500 mt-1">◇</span>
                  <span className="font-reading text-base">{g}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Boolean search suggestions */}
        {r.boolean_search_suggestions?.length > 0 && (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-2">Search Smarter</div>
            <p className="text-xs text-forge-100/55 mb-6 max-w-2xl">
              Copy these Boolean strings into library databases, Google Scholar, EBSCO, JSTOR, or other research tools.
              They are search strategies, not verified results.
            </p>
            <div className="space-y-4" data-testid="research-boolean-searches">
              {r.boolean_search_suggestions.map((suggestion, i) => (
                <article key={`${suggestion.search_string}-${i}`} className="border border-white/10 rounded-sm p-5 bg-forge-800/30 hover:border-gold-500/40 transition-colors">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <code className="font-mono text-sm sm:text-base leading-relaxed text-forge-100 break-words">{suggestion.search_string}</code>
                    <button
                      type="button"
                      onClick={() => copySearch(suggestion.search_string)}
                      className="shrink-0 text-[10px] uppercase tracking-[0.22em] px-3 py-2 border border-white/15 hover:border-ember-500 hover:text-ember-400 rounded-sm transition-all"
                    >
                      {copiedSearch === suggestion.search_string ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Purpose" body={suggestion.purpose} />
                    <Field label="Best for" body={suggestion.best_for} />
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Source evaluation cards */}
        {r.source_recommendations?.length > 0 && (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-2">Where to Look — Source Evaluation</div>
            <p className="text-xs text-forge-100/55 mb-6 max-w-2xl">These are categories of sources to consult — not specific citations. Each card teaches you how to weigh what you find.</p>
            <div className="grid md:grid-cols-2 gap-4" data-testid="research-sources">
              {r.source_recommendations.map((s, i) => (
                <div key={i} className="border border-white/10 rounded-sm p-5 hover:border-ember-500/40 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="font-serif text-lg text-forge-100">{s.type}</div>
                    <span className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 border rounded-sm ${STRENGTH_COLOR[s.evidence_strength] || STRENGTH_COLOR.Moderate}`}>
                      {s.evidence_strength}
                    </span>
                  </div>
                  <Field label="Authority" body={s.authority} />
                  <Field label="Why this strength" body={s.strength_explanation} />
                  <Field label="Perspective" body={s.perspective} pill />
                  <Field label="Recency" body={s.recency_guidance} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Literacy coaching */}
        {r.literacy_coaching?.length > 0 && (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Information Literacy</div>
            <div className="space-y-6" data-testid="research-coaching">
              {r.literacy_coaching.map((c, i) => (
                <div key={i}>
                  <h4 className="font-serif text-lg text-gold-400 mb-1">{c.question}</h4>
                  <p className="font-reading text-base text-forge-100/80 leading-relaxed">{c.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Debate preparation */}
        {r.debate_prep && (r.debate_prep.strongest_supporting || r.debate_prep.strongest_opposing) && (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Debate Preparation</div>
            <div className="grid md:grid-cols-2 gap-px bg-white/5 mb-4" data-testid="research-debate-prep">
              {r.debate_prep.strongest_supporting && (
                <div className="bg-forge-900 p-6">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-ember-500 mb-3">Strongest Supporting</div>
                  <p className="font-reading text-base text-forge-100/90 leading-relaxed">{r.debate_prep.strongest_supporting}</p>
                </div>
              )}
              {r.debate_prep.strongest_opposing && (
                <div className="bg-forge-900 p-6">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-ember-500 mb-3">Strongest Opposing</div>
                  <p className="font-reading text-base text-forge-100/90 leading-relaxed">{r.debate_prep.strongest_opposing}</p>
                </div>
              )}
            </div>
            {r.debate_prep.strongest_unanswered && (
              <div className="border-l-2 border-gold-500 pl-5 py-2" data-testid="research-unanswered">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500 mb-1">Strongest Unanswered Question</div>
                <p className="font-serif text-xl sm:text-2xl text-forge-100 leading-snug">{r.debate_prep.strongest_unanswered}</p>
              </div>
            )}
          </section>
        )}

        {/* Take This Into a Debate */}
        <section className="mb-12 border border-ember-500/30 bg-forge-800/40 rounded-sm p-6 sm:p-8" data-testid="research-handoff">
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Take This Into a Debate</div>
          <p className="font-reading text-base text-forge-100/75 leading-relaxed mb-6 max-w-2xl">
            Carry your investigation into a Socratic dialogue. Choose a stance — ThoughtSmith™ will challenge you fairly,
            never tell you what to think, and afterward generate a Reasoning Report Card on the quality of your thought.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { stance: "supporting", label: "Debate the Supporting Perspective", tone: "ember" },
              { stance: "opposing", label: "Debate the Opposing Perspective", tone: "ember" },
              { stance: "neutral", label: "Debate a Neutral Position", tone: "gold" },
            ].map((b) => (
              <button
                key={b.stance}
                onClick={() => takeToDebate(b.stance)}
                disabled={handoffStance !== null}
                className={`text-left p-4 border rounded-sm transition-all disabled:opacity-50 ${b.tone === "ember" ? "border-ember-500/40 hover:border-ember-500 hover:bg-forge-900 text-ember-400" : "border-gold-500/40 hover:border-gold-500 hover:bg-forge-900 text-gold-400"}`}
                data-testid={`handoff-${b.stance}`}
              >
                <span className="font-serif text-base leading-snug">{handoffStance === b.stance ? "Opening dialogue…" : b.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Footer actions */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-white/5">
          <Link to="/research" className="px-5 py-2.5 border border-white/15 hover:border-ember-500 hover:text-ember-400 text-sm tracking-wide rounded-sm transition-all">← All investigations</Link>
          {r.session_id && (
            <Link to={`/reading/${r.session_id}`} className="px-5 py-2.5 border border-white/15 hover:border-gold-500 hover:text-gold-400 text-sm tracking-wide rounded-sm transition-all">Back to reading</Link>
          )}
          <Link to="/research" className="px-5 py-2.5 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold tracking-wide rounded-sm transition-all">Investigate something else</Link>
        </div>
      </main>
    </div>
  );
}

function ModelBadge({ result }) {
  if (result?.fallback_used) {
    return (
      <span className="inline-flex items-center border border-gold-500/40 bg-gold-500/10 px-2 py-1 text-[10px] uppercase tracking-[0.24em] text-gold-400 rounded-sm">
        Fallback research template used
      </span>
    );
  }
  if (result?.fallback_used === false) {
    return (
      <span className="inline-flex items-center border border-ember-500/30 bg-ember-500/10 px-2 py-1 text-[10px] uppercase tracking-[0.24em] text-forge-100/60 rounded-sm">
        Generated by {result.model_name || "Gemini 2.5 Flash"}
      </span>
    );
  }
  return null;
}

function Detail({ label, body }) {
  if (!body) return null;
  return (
    <div className="mb-3 last:mb-0">
      <div className="text-[10px] uppercase tracking-[0.25em] text-ember-500/80 mb-1">{label}</div>
      <p className="text-sm text-forge-100/85 leading-relaxed">{body}</p>
    </div>
  );
}

function Field({ label, body, pill = false }) {
  if (!body) return null;
  return (
    <div className="mb-2 last:mb-0 text-sm">
      <span className="text-[10px] uppercase tracking-[0.25em] text-forge-100/45 mr-2">{label}</span>
      {pill ? (
        <span className="inline-block px-2 py-0.5 text-xs border border-gold-500/40 text-gold-400 rounded-sm">{body}</span>
      ) : (
        <span className="text-forge-100/80">{body}</span>
      )}
    </div>
  );
}
